import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ButtonGroup, Grid } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  AutoEvaluation,
  AutoEvaluationLabel,
  JobDescription,
  showAllTypes,
} from '../../../../../../utils/types/JobOpenings';
import AddOrRemoveListWithOperators from '../../../ui/AddOrRemoveListWithOperators';
import Collapsible from '../../../ui/CollapsibleChildren';

interface CollapsibleData {
  id: number;
  title: string;
  labels: AutoEvaluationLabel[];
  isOpen: boolean;
  stringLabels?: string[];
}

export type ListFieldsProps = {
  showAll: showAllTypes;
  setShowAll: React.Dispatch<React.SetStateAction<showAllTypes>>;
  autoEvaluation: AutoEvaluation;
  setAutoEvaluation: React.Dispatch<React.SetStateAction<AutoEvaluation>>;
  disabled?: boolean;
  jobDescription: JobDescription;
  evaluationType: 'Auto Suitability' | 'Auto Outstanding';
};

export const ListFields: React.FC<ListFieldsProps> = ({
  showAll,
  setShowAll,
  autoEvaluation,
  setAutoEvaluation,
  disabled = false,
  jobDescription,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const LF = ADMIN_PAGES_KEYS.jobOpenings.listFields;
  const SECTION_TITLE_KEYS: Record<string, string> = {
    'Educations': LF.educations,
    'Certifications': LF.certifications,
    'Skills': LF.skills,
    'School': LF.school,
    'Keywords': LF.keywords,
    'Behavioral Attributes': LF.behavioralAttributes,
  };
  const collapsibleValues = [
    {
      id: 1,
      title: 'Educations',
      labels: autoEvaluation?.educations ?? [],
      isOpen: true,
    },
    {
      id: 2,
      title: 'Certifications',
      labels: autoEvaluation?.certifications ?? [],
      isOpen: true,
    },
    {
      id: 3,
      title: 'Skills',
      labels: autoEvaluation?.skills ?? [],
      isOpen: true,
    },
    {
      id: 4,
      title: 'School',
      labels: autoEvaluation?.school ?? [],
      isOpen: true,
    },
    {
      id: 5,
      title: 'Keywords',
      labels: autoEvaluation?.keywords ?? [],
      isOpen: true,
    },
    {
      id: 6,
      title: 'Behavioral Attributes',
      labels: autoEvaluation?.behavioralAttributes ?? [],
      isOpen: true,
    },
  ];
  const [collapsible, setCollapsible] =
    useState<CollapsibleData[]>(collapsibleValues);

  useEffect(() => {
    const newIsOpenState = getNewIsOpenState(showAll);
    if (newIsOpenState === undefined) return;

    const newCollapsibleValues = collapsibleValues.map((collapsibleItem) => ({
      ...collapsibleItem,
      isOpen: newIsOpenState,
    }));

    setCollapsible((prevCollapsible) => {
      if (
        JSON.stringify(newCollapsibleValues) !== JSON.stringify(prevCollapsible)
      ) {
        return newCollapsibleValues;
      }
      return prevCollapsible;
    });
  }, [autoEvaluation, showAll]);

  useEffect(() => {
    const updatedAutoSuitability = { ...autoEvaluation };

    collapsible.forEach((item) => {
      switch (item.title) {
        case 'Educations':
          updatedAutoSuitability.educations = item.labels;
          break;
        case 'Certifications':
          updatedAutoSuitability.certifications = item.labels;
          break;
        case 'Skills':
          updatedAutoSuitability.skills = item.labels;
          break;
        case 'School':
          updatedAutoSuitability.school = item.labels;
          break;
        case 'Desirable Skills':
          updatedAutoSuitability.desirableSkills = item.labels;
          break;
        case 'Keywords':
          updatedAutoSuitability.keywords = item.labels;
          break;
        case 'Behavioral Attributes':
          updatedAutoSuitability.behavioralAttributes = item.labels;
          break;
        default:
          break;
      }
    });

    setAutoEvaluation(updatedAutoSuitability);
  }, [collapsible]);

  const getNewIsOpenState = (showAll: showAllTypes) => {
    if (showAll === 'showAll') {
      return true;
    } else if (showAll === 'collapseAll') {
      return false;
    }
    return;
  };

  const toggleOpen = (id: number) => {
    const newCollapsible = collapsible.map((collapsible) =>
      collapsible.id === id
        ? { ...collapsible, isOpen: !collapsible.isOpen }
        : collapsible,
    );
    setCollapsible(newCollapsible);
    const areAllOpen = newCollapsible.every(
      (collapsible) => collapsible.isOpen,
    );
    const areAllClosed = newCollapsible.every(
      (collapsible) => !collapsible.isOpen,
    );
    if (areAllOpen) {
      setShowAll('showAll');
    } else if (areAllClosed) {
      setShowAll('collapseAll');
    } else {
      setShowAll('random');
    }
  };

  const handleLabelsChange = (
    id: number,
    updatedLabels: AutoEvaluationLabel[],
  ) => {
    setCollapsible(
      collapsible.map((collapsible) =>
        collapsible.id === id
          ? { ...collapsible, labels: updatedLabels }
          : collapsible,
      ),
    );
  };

  const handleBulkToggleOperator = (id: number, operator: 'AND' | 'OR') => {
    const section = collapsible.find((c) => c.id === id);
    if (!section) return;

    const updatedLabels = section.labels.map((label) => ({
      ...label,
      relationToNext: operator,
    }));

    handleLabelsChange(id, updatedLabels);
  };

  const LogicToggle = ({
    id,
    labels,
  }: {
    id: number;
    labels: AutoEvaluationLabel[];
  }) => {
    if (labels.length < 2) return null;

    const isAllAnd = labels.every((l) => l.relationToNext === 'AND');
    const isAllOr = labels.every((l) => l.relationToNext === 'OR');

    return (
      <ButtonGroup size='small' sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 0.25 }}>
        <Button
          onClick={() => handleBulkToggleOperator(id, 'AND')}
          variant={isAllAnd ? 'contained' : 'text'}
          color={isAllAnd ? 'success' : 'inherit'}
          sx={{
            py: 0.2,
            px: 1,
            fontSize: '0.65rem',
            fontWeight: 800,
            borderRadius: 1,
            minWidth: 'unset',
          }}
        >
          AND
        </Button>
        <Button
          onClick={() => handleBulkToggleOperator(id, 'OR')}
          variant={isAllOr ? 'contained' : 'text'}
          color={isAllOr ? 'success' : 'inherit'}
          sx={{
            py: 0.2,
            px: 1,
            fontSize: '0.65rem',
            fontWeight: 800,
            borderRadius: 1,
            minWidth: 'unset',
          }}
        >
          OR
        </Button>
      </ButtonGroup>
    );
  };

  return (
    <Grid container spacing={2}>
      {collapsible.map(({ id, title, labels, isOpen }) => (
        <Grid item xs={12} md={6} lg={4} key={id}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Collapsible
              title={`${t(SECTION_TITLE_KEYS[title] ?? title)} ${labels.length > 0 ? `(${labels.length})` : ''}`}
              isOpen={isOpen}
              toggleOpen={() => toggleOpen(id)}
              headerActions={<LogicToggle id={id} labels={labels} />}
            >
              <AddOrRemoveListWithOperators
                disabled={disabled}
                key={JSON.stringify(labels)}
                initialLabels={labels}
                onLabelsChange={(updatedLabels) =>
                  handleLabelsChange(id, updatedLabels)
                }
                desirableSkills={
                  title === 'Skills' ? jobDescription.desirableSkills : undefined
                }
              />
            </Collapsible>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default ListFields;
